import { motion } from 'framer-motion'
import { Clock, Users, CheckCircle, AlertCircle } from 'lucide-react'
import { DanceClass, Schedule } from '../core'
import { useScheduleData } from '../hooks'
import { isScheduleInFuture, isValidScheduleTime, formatTimeRangeWithAMPM } from '../core'

interface TimeSlotSelectorProps {
  danceClass: DanceClass | null
  selectedDate: string
  selectedSchedule: Schedule | null
  onScheduleSelect: (schedule: Schedule) => void
  isDemo?: boolean
}

export default function TimeSlotSelector({ 
  danceClass, 
  selectedDate, 
  selectedSchedule, 
  onScheduleSelect,
  isDemo = false
}: TimeSlotSelectorProps) {
  // Use custom hook for schedule data
  const { 
    allClassSchedules,
    isLoading, 
    error 
  } = useScheduleData({ 
    danceClass, 
    isDemo
  })

  // Get all future schedules for the selected date (including those within 1 hour)
  const allFutureSchedules = selectedDate ? 
    allClassSchedules.filter(schedule => 
      schedule.specificDate === selectedDate && 
      isScheduleInFuture(schedule.specificDate, schedule.startTime)
    ) : []

  // Mark schedules within 1 hour as disabled
  const availableSchedules = allFutureSchedules.map(schedule => ({
    ...schedule,
    isWithinBuffer: !isValidScheduleTime(schedule.specificDate, schedule.startTime)
  }))

  // Helper function to create a date in IST timezone
  const createISTDate = (year: number, month: number, day: number): Date => {
    return new Date(year, month - 1, day) // month is 0-indexed in Date constructor
  }

  const handleTimeSlotClick = (schedule: Schedule) => {
    onScheduleSelect(schedule)
  }

  if (!danceClass || !selectedDate) return null

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-gray-600">
        <Clock className="w-4 h-4" />
        <span>Available times for {(() => {
          const [year, month, day] = selectedDate.split('-').map(Number)
          const date = createISTDate(year, month, day)
          return date.toLocaleDateString('en-IN', {
            timeZone: 'Asia/Kolkata'
          })
        })()}</span>
      </div>
      
      {isLoading ? (
        <div className="flex items-center justify-center py-4">
          <div className="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-2 text-sm text-gray-600">Loading time slots...</span>
        </div>
      ) : error ? (
        <div className="text-center py-4">
          <div className="text-red-600 text-sm mb-1">Service temporarily unavailable</div>
          <div className="text-gray-500 text-xs">Please try again after some time</div>
        </div>
      ) : availableSchedules.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {availableSchedules.map((schedule) => {
            const isDisabled = schedule.isFullyBooked || schedule.isWithinBuffer
            const isSelected = selectedSchedule?.id === schedule.id
            
            return (
              <motion.button
                key={schedule.id}
                type="button"
                onClick={() => !isDisabled && handleTimeSlotClick(schedule)}
                disabled={isDisabled}
                className={`
                  p-3 rounded-lg border-2 transition-all duration-200 text-left relative
                  ${isDisabled
                    ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                    : isSelected
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-primary-25'
                  }
                `}
                whileHover={!isDisabled ? { scale: 1.02 } : {}}
                whileTap={!isDisabled ? { scale: 0.98 } : {}}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {formatTimeRangeWithAMPM(schedule.startTime, schedule.endTime)}
                    </div>
                    <div className="text-sm text-gray-500 flex items-center gap-2 mt-1">
                      <Users className="w-3 h-3" />
                      <span className={schedule.isFullyBooked ? 'text-red-600 font-semibold' : 'text-gray-500'}>
                        {schedule.isFullyBooked 
                          ? 'Fully Booked' 
                          : `${schedule.remainingSpots || 0} spots remaining`
                        }
                      </span>
                      <span className="text-gray-400">
                        (Max {schedule.maxCapacity})
                      </span>
                      {schedule.isDemo && (
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                          Demo
                        </span>
                      )}
                    </div>
                    {schedule.isWithinBuffer && (
                      <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                        <AlertCircle className="w-3 h-3" />
                        <span>Cannot book within 1 hour</span>
                      </div>
                    )}
                  </div>
                  {isSelected && !isDisabled && (
                    <CheckCircle className="w-4 h-4 text-primary-600" />
                  )}
                </div>
              </motion.button>
            )
          })}
        </div>
      ) : (
        <div className="text-center py-6 text-gray-500">
          <Clock className="w-8 h-8 mx-auto mb-2 text-gray-400" />
          <p className="text-sm">No available time slots for this date</p>
        </div>
      )}
    </div>
  )
}
